import React, { useState, useEffect } from 'react';
import { markCompleteACP } from '@api/resident';
import { ResidentDetails } from '@typings/ResidentTypes';
import { Role } from '@typings/ProfileTypes';
import './styles.scss';

export interface AcpCompletionPanel {
  type: string;
  title: string;
  content: string;
  isShow: boolean;
}
interface AdvanceCarePlanCompletionProps {
  role: string;
  userId: string;
  residentDetails: ResidentDetails | undefined;
  setAcpCompletionPanel: (acpPanel: AcpCompletionPanel) => void;
}

const AdvanceCarePlanCompletion: React.FC<AdvanceCarePlanCompletionProps> = ({
  role,
  userId,
  residentDetails,
  setAcpCompletionPanel,
}) => {
  const [isChecked, setIsChecked] = useState(false);
  const [acpStatus, setAcpStatus] = useState('');

  useEffect(() => {
    setAcpStatus(residentDetails?.profile.acp as string);
  }, [residentDetails]);

  useEffect(() => {
    setIsChecked(acpStatus === 'completed');
  }, [acpStatus]);

  const handleCheckboxChange = async () => {
    const previousState = isChecked;
    setIsChecked(!isChecked);
    try {
      const response = await markCompleteACP(userId);
      if (
        response &&
        response.status === 'success' &&
        response.data.acp === 'completed'
      ) {
        setAcpCompletionPanel({
          type: 'completed',
          title: 'This Advance Care Plan has been Completed',
          content: '',
          isShow: true,
        });
      } else if (
        response &&
        response.status === 'success' &&
        response.data.acp === 'inprogress'
      ) {
        setAcpCompletionPanel({
          type: 'inprogress',
          title: 'This Advance Care Plan is now logged as In Progress',
          content: '',
          isShow: true,
        });
      } else {
        setIsChecked(previousState);
      }
    } catch (error) {
      setIsChecked(previousState);
      console.error(error);
    }
  };

  return (
    <div className="acp-completion px-3 pt-3 pb-0.5 mb-10 mt-5 govuk-bp:mt-6">
      <h3 className="govuk-heading-m">Advance Care Plan - Completion</h3>
      <p className="govuk-body">
        Advance Care Planning is an ongoing process. Information and
        documentation can be updated whenever the person&apos;s circumstances
        change and whenever it is suitable to do so. When the document process
        is completed for the first time please check the box below.
      </p>
      <div className="govuk-form-group">
        <fieldset
          className="govuk-fieldset"
          aria-describedby="asigned-nok-hint"
        >
          <div className="govuk-checkboxes" data-module="govuk-checkboxes">
            <div className="govuk-checkboxes__item">
              <input
                className="govuk-checkboxes__input"
                id="acp-completion"
                name="acp-copletion"
                type="checkbox"
                disabled={!acpStatus || role === Role.TeamMemberViewOnly}
                checked={isChecked}
                onChange={handleCheckboxChange}
              />
              <label
                className="govuk-label govuk-checkboxes__label"
                htmlFor="acp-completion"
              >
                The first version of this Advance Care Planning document has
                been completed.
              </label>
            </div>
          </div>
        </fieldset>
        {!acpStatus && (
          <div className="govuk-warning-text mt-3 md:mt-5">
            <span className="govuk-warning-text__icon" aria-hidden="true">
              !
            </span>
            <strong className="govuk-warning-text__text">
              <span className="govuk-visually-hidden">Warning</span>
              You haven&apos;t completed this person&apos;s Advance Care Plan
            </strong>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceCarePlanCompletion;
